import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from 'styled-components';
import { cleanDMImageUrl, fetchDMArtifact, isDMIncludeLink } from '../services/DigitaltMuseum';
import { AppContext } from '../contexts/AppContext';
import useFetchMedia from '../hooks/useFetchMedia';
import DMObjectModal from './DMObjectModal';
import BlockText from './BlockText';
import BlockImageSlideshow from './BlockMedia_Slideshow';
import { BlockAudioItem, BlockAudioWrapper } from './BlockAudio';

interface BlockDMFieldsProps {
  /** An item in the block's `dmBlocks`: the Kulturio media ID of the DM object and the fields to show (`includeFields`) */
  element?: any,
  /** includeLink from the block (a boolean or "true"): a tap on an image opens the DM object popup instead of the lightbox */
  includeLink?: any
}

/** A field to show, in the order chosen in the CMS */
type DMField =
  | { type: "text", text?: string }
  | { type: "images", images: { mediaId?: string, media: any }[] }
  | { type: "video" | "audio", dmsId: string };

/** The DM object's files of a type. Files without a type count as the object's type (e.g. an Audio object) */
const filesOfType = (artifact: any, type: "audio" | "video") => (artifact?.files || [])
  .filter((file: any) => file?.mediaId && ((file?.fileType || "").toLowerCase() === type || (file?.fileType == null && artifact?.type?.toLowerCase() === type)));

/**
 * Pick the fields chosen in the CMS from the DM object
 * @param {object[]} includeFields The block item's fields, with `imageIds` for images (DMS media IDs, or indexes)
 * @param {object} artifact DM object (simple_json)
 * @returns {DMField[]} Fields to show
 */
const getFields = (includeFields: any[], artifact: any): DMField[] => {
  const owner = artifact?.partOfCollection?.owner?.name;
  return includeFields.flatMap((field: any): DMField[] => {
    switch (field?.field) {
      case "title": return [{ type: "text", text: artifact?.title }];
      case "motif": return [{ type: "text", text: artifact?.motif?.description }];
      case "history": return [{ type: "text", text: artifact?.history?.description }];
      case "description": return [{ type: "text", text: artifact?.description }];
      case "owner": return [{ type: "text", text: owner }];
      case "audio":
      case "video":
        return filesOfType(artifact, field.field).map((file: any) => ({ type: field.field, dmsId: file.mediaId }));
      case "images": {
        const all: any[] = artifact?.images || [];
        const chosen = (field?.imageIds || []).flatMap((id: any) => all.filter((image, i) => id === image?.mediaId || id === i));
        return [{
          type: "images",
          images: chosen.map(image => ({
            mediaId: image?.mediaId,
            media: {
              url: cleanDMImageUrl(image?.url),
              media_type: "dm",
              credits: [{ credit_type_id: 1, name: image?.photographer }, { credit_type_id: 6, name: owner }]
            }
          }))
        }];
      }
      default: return [];
    }
  });
}

/**
 * Chosen fields from a single DigitaltMuseum object (title, texts, images, audio, video), as article blocks.
 * With includeLink a tap on an image opens the DM object popup at that image, otherwise the lightbox
 * @returns {JSX.Element} Component template
 */
const BlockDMFields: FC<BlockDMFieldsProps> = ({ element, includeLink }) => {
  const theme = useTheme();
  const { idleResetCount } = useContext(AppContext);
  const [mediaObject] = useFetchMedia({ mediaId: element?.mediaId });
  const [artifact, setArtifact] = useState<any>();
  // The popup, starting on the tapped image. Null when closed
  const [popup, setPopup] = useState<{ mediaId?: string } | null>(null);
  const uniqueId: string | undefined = mediaObject?.identifier;

  // Get the object from DM
  useEffect(() => {
    if (!uniqueId) { return; }
    let isActive = true;
    fetchDMArtifact(uniqueId)
      .then(data => { isActive && setArtifact(data); })
      .catch(() => console.error("Could not fetch data from DM"));
    return () => { isActive = false; };
  }, [uniqueId]);

  // Close the popup when idle mode resets the kiosk
  useEffect(() => { setPopup(null); }, [idleResetCount]);

  const fields = useMemo(() => artifact ? getFields(element?.includeFields || [], artifact) : [], [artifact, element?.includeFields]);
  const canOpenObject = Boolean(uniqueId) && isDMIncludeLink(element?.includeLink ?? includeLink);

  if (fields.length === 0) { return null; }

  return (
    <>
      {fields.map((field, i) => {
        switch (field.type) {
          case "text":
            return field.text ? <BlockText key={`block${i}`} text={field.text}/> : null;
          case "images":
            return field.images.length > 0 && (
              <BlockImageSlideshow key={`block${i}`} elements={field.images.map(image => image.media)}
                onOpen={canOpenObject ? (index: number) => setPopup({ mediaId: field.images[index]?.mediaId }) : undefined}/>
            );
          case "video":
            return <BlockImageSlideshow key={`block${i}`} elements={[{ dmsId: field.dmsId, media_type: "video" }]}/>;
          case "audio":
            return <BlockAudioWrapper key={`block${i}`}><BlockAudioItem media={{ dmsId: field.dmsId, media_type: "audio" }}/></BlockAudioWrapper>;
          default:
            return null;
        }
      })}

      {/* In body, so the popup covers the whole screen whatever transforms the article has */}
      {popup && uniqueId && createPortal(
        <DMObjectModal uniqueId={uniqueId} backgroundColor={theme?.backgroundColor} onClose={() => setPopup(null)} initialMediaId={popup.mediaId}
          preview={{ uniqueId, uuid: artifact?.uuid, title: artifact?.title, owner: artifact?.partOfCollection?.owner?.name, mediaIdentifier: popup.mediaId }}/>,
        document.body
      )}
    </>
  );
}

export default BlockDMFields;
